import React, { useState } from 'react';
import { LazyLoadImage as OriginalLazyLoadImage, LazyLoadImageProps } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Box, SxProps, Theme } from '@mui/material';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Cancel } from '@mui/icons-material';
import { NoImage } from './NoImage';

export interface CustomLazyLoadImageProps extends LazyLoadImageProps {
    showGradient?: boolean;
    gradientStyle?: SxProps<Theme>;
    noImageStyle?: object;
    withPreview?: boolean;
    isEditMode?: boolean;
    onEditClick?: () => void;
}

const CustomLazyLoadImage: React.FC<CustomLazyLoadImageProps> = ({
    noImageStyle,
    showGradient,
    style,
    gradientStyle,
    src,
    withPreview,
    isEditMode,
    onEditClick,
    ...props
}) => {
    const [hover, setHover] = useState(false);

    const imageComponent = (
        <Box
            position="relative"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            sx={{ cursor: 'pointer', display: 'inline-block', position: 'relative', overflow: 'visible' }}>
            {!!src && <OriginalLazyLoadImage src={src} style={style} {...props} />}
            {isEditMode && (
                <Cancel
                    fontSize="large"
                    onClick={e => {
                        e.stopPropagation(); // Prevent PhotoView from capturing the click event
                        if (onEditClick) {
                            onEditClick(); // Trigger the edit mode action
                        }
                    }}
                    sx={{
                        position: 'absolute',
                        zIndex: 99999,
                        top: 0,
                        right: 0,
                        cursor: 'pointer',
                        color: 'white',
                    }}
                />
            )}
            {!src && (
                <NoImage
                    sx={{
                        height: '100%',
                        width: '100%',
                        maxHeight: '40px',
                        maxWidth: '40px',
                        objectFit: 'cover',
                        opacity: 1,
                        ...noImageStyle,
                    }}
                />
            )}
            {hover && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'opacity 0.3s ease-in-out',
                        ...style,
                    }}>
                    <ZoomInIcon sx={{ color: 'white', fontSize: 30 }} />
                </Box>
            )}
            {showGradient && (
                <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    width="100%"
                    height="80%"
                    sx={{
                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)',
                        pointerEvents: 'none',
                        ...gradientStyle,
                    }}
                />
            )}
        </Box>
    );

    const normalComponent = (
        <>
            {!!src && <OriginalLazyLoadImage src={src} style={style} {...props} />}
            {!src && (
                <NoImage
                    sx={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                        opacity: 1,
                        ...noImageStyle,
                    }}
                />
            )}
            {showGradient && (
                <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    width="100%"
                    height="80%"
                    sx={{
                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)',
                        pointerEvents: 'none',
                        ...gradientStyle,
                    }}
                />
            )}
        </>
    );

    return withPreview && src ? (
        <PhotoProvider>
            <PhotoView src={src}>{imageComponent}</PhotoView>
        </PhotoProvider>
    ) : (
        normalComponent
    );
};

export default CustomLazyLoadImage;
